import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../context/LanguageContext";
import translations from "../i18n/translations";

// HERO + fallback slike
import heroImage from "../assets/glavna.jpeg";
import oldPeople from "../assets/apropos1.jpeg";
import organizePlan from "../assets/sarl3.jpeg";
import personalAssist from "../assets/sarl1.jpeg";
import reservations from "../assets/sarl4.jpeg";
import transport from "../assets/sarl5.jpeg";
import shopping from "../assets/iStock-1213744660v3.jpg";

const serviceImages = [
  personalAssist,
  organizePlan,
  reservations,
  transport,
  shopping,
];

export default function Home() {
  const { lang } = useLanguage();
  const t = translations[lang].home;

  const servicesList = t.servicesList.map((s, i) => ({
    ...s,
    img: serviceImages[i],
  }));

  const testimonialsList = t.testimonials || [];
  const specialOffersList = t.specialOffers || [];

  // ---------------- WELCOME (TYPE ONCE) ----------------
  const [typedText, setTypedText] = useState("");
  const hasTyped = useRef(false);

  const welcomeText = t.welcome || "";

  useEffect(() => {
    setTypedText("");
    hasTyped.current = false;

    let i = 0;

    const type = () => {
      if (i <= welcomeText.length) {
        setTypedText(welcomeText.slice(0, i));
        i++;
        setTimeout(type, 80);
      }
    };

    if (!hasTyped.current) {
      hasTyped.current = true;
      type();
    }
  }, [lang, welcomeText]);

  // ---------------- TESTIMONIALS ----------------
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    if (!testimonialsList.length) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonialsList.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [testimonialsList.length]);

  // ---------------- SPECIAL OFFERS ----------------
  const [currentOffer, setCurrentOffer] = useState(0);

  useEffect(() => {
    if (!specialOffersList.length) return;

    const interval = setInterval(() => {
      setCurrentOffer((prev) => (prev + 1) % specialOffersList.length);
    }, 4500);

    return () => clearInterval(interval);
  }, [specialOffersList.length]);

  return (
    <div className="overflow-x-hidden">

      {/* ---------------- HERO ---------------- */}
      <section className="relative w-full h-[70vh] md:h-[90vh]">
        <img
          src={heroImage}
          alt="Hero"
          className="w-full h-full object-cover object-center md:object-right"
        />

        <div className="absolute inset-0 bg-black/40 flex flex-col justify-center px-4 md:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-xl space-y-6 text-white"
          >

            {/* WELCOME TYPE */}
            <h2 className="text-xl md:text-2xl font-light opacity-90 min-h-[40px]">
              {typedText}
              <span className="animate-pulse">|</span>
            </h2>

            <h1 className="text-3xl md:text-5xl font-bold">
              HelvetAssist
            </h1>

            <p className="text-base md:text-lg opacity-90">
              {t.heroSubtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/services"
                className="bg-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition text-center"
              >
                {t.heroBtnServices1}
              </Link>

              <Link
                to="/contact"
                className="border border-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-50 transition text-center"
              >
                {t.heroBtnContact}
              </Link>
            </div>

          </motion.div>
        </div>
      </section>

      {/* ---------------- SPECIAL OFFERS (MELT + IMAGE FROM TRANSLATIONS) ---------------- */}
      <section className="py-20 px-4 md:px-20 bg-white">
        <div className="max-w-6xl mx-auto">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold">
              {t.offersTitle}
            </h2>
            <p className="text-gray-600 mt-2">
              {t.offersSubtitle}
            </p>
          </div>

          {specialOffersList.length > 0 && (
            <div className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-3xl shadow-2xl overflow-hidden grid md:grid-cols-2 min-h-[380px]">

              {/* IMAGE */}
              <div className="relative h-72 sm:h-80 md:h-auto overflow-hidden">
               <AnimatePresence mode="wait">
  <motion.img
    key={currentOffer}
    src={specialOffersList[currentOffer].img || heroImage}
    alt="Special offer"
    initial={{ opacity: 0, x: 40 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -40 }}
    transition={{ duration: 0.5 }}
    className="w-full h-full object-cover scale-110"
  />
</AnimatePresence>

                <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-white/40" />
              </div>

              {/* TEXT */}
              <div className="p-6 md:p-10 flex flex-col justify-center relative">

                <div className="absolute inset-0 bg-gradient-to-l from-white/40 to-transparent pointer-events-none" />

                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentOffer}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="relative"
                  >

                    <span className="text-sm font-semibold text-red-600 uppercase">
                      {specialOffersList[currentOffer].tag}
                    </span>

                    <h3 className="mt-2 text-2xl md:text-3xl font-bold">
                      {specialOffersList[currentOffer].title}
                    </h3>

                    <div className="mt-4 min-h-[90px]">
                      <p className="text-gray-700 leading-relaxed">
                        {specialOffersList[currentOffer].text}
                      </p>
                    </div>

                    <Link
                      to={specialOffersList[currentOffer].href}
                      className="inline-block mt-6 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition w-fit"
                    >
                      {t.specialOfferBtn}
                    </Link>

                  </motion.div>
                </AnimatePresence>

              </div>
            </div>
          )}
        </div>
      </section>

      {/* ---------------- ABOUT ---------------- */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">

          <img
          
  src={oldPeople}
  alt="Old people"
  className="rounded-xl w-full max-h-[500px] object-cover"
          />

          <div className="space-y-6">
            <h2 className="text-4xl font-bold">{t.aboutTitle}</h2>
            <p className="text-gray-700">{t.aboutText1}</p>
            <p className="text-gray-700">{t.aboutText2}</p>

            <Link
              to="/about"
              className="text-red-600 font-semibold hover:underline"
            >
              {t.aboutLink}
            </Link>
          </div>

        </div>
      </section>


 {/* ---------------- SERVICES ---------------- */}
<section className="bg-gray-50 py-20">
  <div className="max-w-6xl mx-auto px-6">

    <h2 className="text-3xl md:text-4xl mb-4 font-bold">
      {t.servicesTitle}
    </h2>

    {/* SWIPE HINT */}
    <p className="text-sm text-gray-500 mb-6 text-center animate-pulse">
      ← balayer / faire défiler →
    </p>

    {/* SLIDER WRAPPER */}
    <div className="relative">

      {/* LEFT FADE */}
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none" />

      {/* RIGHT FADE */}
      <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none" />

      {/* SCROLL AREA */}
      <div className="flex overflow-x-auto gap-6 snap-x snap-mandatory pb-6 scroll-smooth px-2">

        {servicesList.map((s, i) => (
          <div
            key={i}
            className="
              min-w-[85%] sm:min-w-[70%] md:min-w-[320px]
              snap-center bg-white rounded-2xl shadow-lg
              overflow-hidden hover:shadow-2xl
              transition-transform duration-300 hover:scale-[1.02]
            "
          >

            <img
              src={s.img}
              alt={s.title}
              className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
            />

            <div className="p-5">
              <h3 className="text-xl font-semibold text-red-600 mb-2">
                {s.title}
              </h3>

              <p className="text-gray-600 leading-relaxed">
                {s.desc}
              </p>
            </div>

          </div>
        ))}

        {/* 👉 SUBTLE FINAL ARROW LINK */}
        <Link
          to="/services"
          className="
            min-w-[85%] sm:min-w-[70%] md:min-w-[320px]
            snap-center flex items-center justify-center
            text-gray-400 hover:text-red-600
            transition
          "
        >
          <div className="text-center p-10">
            <div className="text-lg font-medium mb-2">
              Voir tous les services
            </div>

            <div className="text-3xl opacity-60 hover:opacity-100 transition">
              →
            </div>
          </div>
        </Link>

      </div>
    </div>

  </div>
</section>
   

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="py-28 bg-white">
        <div className="max-w-xl mx-auto text-center px-6">

          <h2 className="text-4xl font-bold text-red-600 mb-12">
            {t.testimonialsTitle}
          </h2>

          <div className="relative h-[300px] overflow-hidden">
            {testimonialsList.map((item, i) => (
              <div
                key={i}
                className={`absolute inset-0 flex flex-col justify-center items-center transition-all duration-700 ${
                  i === currentTestimonial ? "opacity-100" : "opacity-0"
                }`}
              >
                <div className="bg-red-600 text-white p-10 rounded-2xl shadow-xl w-full">
                  <p className="text-xl italic mb-4">“{item.text}”</p>
                  <div className="font-semibold">— {item.author}</div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="bg-red-600 py-20 text-center text-white">
        <h2 className="text-4xl font-bold mb-4">{t.ctaTitle}</h2>
        <p className="mb-8">{t.ctaText}</p>

        <Link
          to="/contact"
          className="bg-white text-red-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
        >
          {t.ctaButton}
        </Link>
      </section>

    </div>
  );
}